import React, { useState, useMemo } from 'react';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';

const TRUNCATE_AT = 60;

const styles = {
    wrapper: { overflowX: 'auto', fontFamily: '"Segoe UI", sans-serif', fontSize: '13px' },
    table: { width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' },
    thead: { position: 'sticky', top: 0, zIndex: 1 },
    th: {
        background: '#f3f3f3',
        borderBottom: '1px solid #d0d0d0',
        padding: '6px 12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        color: '#333',
        textAlign: 'left',
    },
    thSortable: {
        background: '#f3f3f3',
        borderBottom: '1px solid #d0d0d0',
        padding: '6px 12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        color: '#333',
        textAlign: 'left',
        cursor: 'pointer',
        userSelect: 'none',
    },
    tdEven: { padding: '5px 12px', borderBottom: '1px solid #ebebeb', background: '#fff', verticalAlign: 'top', color: '#1a1a1a' },
    tdOdd: { padding: '5px 12px', borderBottom: '1px solid #ebebeb', background: '#f9f9f9', verticalAlign: 'top', color: '#1a1a1a' },
    toggle: { color: '#0078d4', cursor: 'pointer', background: 'none', border: 'none', padding: '0 0 0 4px', fontSize: '12px' },
    pageBtn: (disabled) => ({
        padding: '3px 12px', borderRadius: '4px', border: '1px solid #006bd3',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: disabled ? '#f0f0f0' : '#fff',
        color: disabled ? '#aaa' : '#006bd3',
    }),
};

function TruncatedCell({ value }) {
    const [expanded, setExpanded] = useState(false);
    const text = String(value ?? '');
    if (text.length <= TRUNCATE_AT) return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;

    return expanded ? (
        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block', minWidth: '300px' }}>
            {text}
            <button style={styles.toggle} onClick={() => setExpanded(false)}>less</button>
        </span>
    ) : (
        <span style={{ whiteSpace: 'nowrap' }}>
            {text.slice(0, TRUNCATE_AT)}...
            <button style={styles.toggle} onClick={() => setExpanded(true)}>more</button>
        </span>
    );
}

function ActivityLogDataTable({ events, pageSize = 25 }) {
    const [sortDirection, setSortDirection] = useState('descending');
    const [page, setPage] = useState(1);

    const dimKeys = useMemo(() => [...new Set(events.flatMap(e => Object.keys(e.customDimensions || {})))], [events]);

    const handleTimestampSort = () => {
        setSortDirection(prev => prev === 'ascending' ? 'descending' : 'ascending');
        setPage(1);
    };

    const sortedEvents = useMemo(() => [...events].sort((a, b) => {
        if (a.timestamp < b.timestamp) return sortDirection === 'ascending' ? -1 : 1;
        if (a.timestamp > b.timestamp) return sortDirection === 'ascending' ? 1 : -1;
        return 0;
    }), [events, sortDirection]);

    const totalPages = Math.ceil(sortedEvents.length / pageSize);
    const pagedEvents = sortedEvents.slice((page - 1) * pageSize, page * pageSize);

    const cols = ['timestamp', 'eventName', ...dimKeys];

    return (
        <div>
            <div style={styles.wrapper}>
                <table style={styles.table}>
                    <thead style={styles.thead}>
                        <tr>
                            {cols.map(col => col === 'timestamp' ? (
                                <th key={col} style={styles.thSortable} onClick={handleTimestampSort}>
                                    Timestamp {sortDirection === 'ascending' ? <FaArrowUp style={{ marginLeft: 4, fontSize: 10 }} /> : <FaArrowDown style={{ marginLeft: 4, fontSize: 10 }} />}
                                </th>
                            ) : (
                                <th key={col} style={styles.th}>
                                    {col === 'eventName' ? 'Event Name' : col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pagedEvents.map((event, i) => {
                            const td = i % 2 === 0 ? styles.tdEven : styles.tdOdd;
                            return (
                                <tr key={(page - 1) * pageSize + i}>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(event.timestamp).toLocaleString()}</td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{event.eventName}</td>
                                    {dimKeys.map(key => (
                                        <td key={key} style={td}>
                                            <TruncatedCell value={event.customDimensions?.[key]} />
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '13px' }}>
                    <button style={styles.pageBtn(page === 1)} disabled={page === 1} onClick={() => setPage(1)}>«</button>
                    <button style={styles.pageBtn(page === 1)} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                    <span>Page {page} of {totalPages}</span>
                    <button style={styles.pageBtn(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
                    <button style={styles.pageBtn(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                    <span style={{ color: '#888' }}>({sortedEvents.length} total)</span>
                </div>
            )}
        </div>
    );
}

export default ActivityLogDataTable;
